import { resetLoadedBooks } from "@/data/redux/armySlice";
import { IFtlState, loadFleet, loadSavedShips } from "@/data/redux/ftlSlice";
import { ListState, loadSavedList } from "@/data/redux/listSlice";
import { AppDispatch, RootState } from "@/data/redux/store";
import {
  IFleetData,
  IFleetShip,
  IFleetShipSelection,
  IFtlSavedListState,
  ISaveData,
  IShipWeapon,
  ShipClass,
  ShipWeaponFacing
} from "@/data/types";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { uniq, uniqBy } from "lodash-es";
import FtlService from "./FtlService";
import PersistenceService from "./PersistenceService";

export default class FtlPersistenceService {
  public static currentSaveVersion = 1;

  public static async loadFtlFromKey(dispatch: AppDispatch, key: string, callback: () => void) {
    const saveKey = PersistenceService.getSaveKey(key);
    const save = JSON.parse(localStorage.getItem(saveKey) ?? sessionStorage.getItem(saveKey));
    this.load(dispatch, save, callback);
  }

  public static async loadFromShare(
    dispatch: ThunkDispatch<RootState, any, any>,
    shareData: IFtlSavedListState,
    callback: () => void
  ) {
    console.log("Loading GFWF save...", shareData);

    const fleetIds = uniq(shareData.ships.map((x) => x.fleetId));
    const promises = fleetIds.filter((x) => x !== "common").map((fleetId) => dispatch(loadFleet(fleetId)));

    Promise.all(promises).then((results) => {
      const fleets = uniqBy(
        results.flatMap((res) => res.payload as IFleetData),
        (x) => x.uid
      );
      const { list, loadedShips } = this.buildFtlListFromSave(shareData, fleets);

      dispatch(loadSavedList({ ...list, isCloud: true }));
      dispatch(loadSavedShips(loadedShips));
      callback();
    });

    callback();
  }

  public static async load(
    dispatch: ThunkDispatch<RootState, any, any>,
    save: ISaveData,
    callback: (save: ISaveData) => void
  ) {
    dispatch(resetLoadedBooks());

    Promise.all(
      save.armyIds.filter((x) => x !== "common").map((fleetId) => dispatch(loadFleet(fleetId)))
    ).then((results) => {
      const fleets = uniqBy(
        results.flatMap((res) => res.payload as IFleetData),
        (x) => x.uid
      );
      const { list, loadedShips } = this.buildFtlListFromSave(save.list as IFtlSavedListState, fleets);

      dispatch(loadSavedList(list));
      dispatch(loadSavedShips(loadedShips));
      callback(save);
    });
  }

  private static buildFtlListFromSave(savedList: IFtlSavedListState, fleets: IFleetData[]) {
    const { ships: _ships, ...list } = savedList;
    const loadedShips = savedList.ships.map((savedShip) => {
      const heroes = fleets.flatMap((x) => x.heroes);
      const titles = fleets.flatMap((x) => x.titles);
      const ships = fleets.flatMap((x) => x.ships);
      const upgrades = fleets.flatMap((x) => x.upgrades);
      const weapons = fleets.flatMap((x) => x.weapons);
      const shipDefinition: IFleetShip = ships.find((x) => x.id === savedShip.id);
      const ship: IFleetShipSelection = {
        ...shipDefinition,
        ...savedShip,
        title: titles.find((x) => x.id === savedShip.title) || null,
        hero: heroes.find((x) => x.id === savedShip.hero) || null,
        systems: savedShip.systems
          ?.map((x) => {
            const definition = upgrades.find((upg) => upg.id === x.id);
            if (!definition) return null;
            return {
              ...definition,
              damage: x.damage
            };
          })
          .filter((x) => !!x),
        weapons: savedShip.weapons
          ?.map((x) => {
            const definition = weapons.find((w) => w.id === x.id);
            if (!definition) return null;
            return {
              ...definition,
              facing: x.facing,
              damage: x.damage
            };
          })
          .filter((x) => !!x)
      };
      return ship;
    });

    return { list, loadedShips };
  }

  public static updateSave(list: ListState, ships: IFleetShipSelection[]) {
    return this.updateSaveData(list.id, (existingSave) => {
      const points: number = FtlService.calculateListTotal(ships);

      const saveData: ISaveData = {
        ...existingSave,
        modified: new Date().toJSON(),
        listPoints: points,
        list: PersistenceService.getDataForFtlSave(list, ships),
        saveVersion: this.currentSaveVersion
      };

      console.log("Updating save...", saveData);

      return saveData;
    });
  }

  private static updateSaveData(listId: string, modifySaveFunc: (save: ISaveData) => ISaveData) {
    const key = PersistenceService.getSaveKey(listId);
    const localSave = localStorage[key];
    if (!localSave) return;

    const saveData = modifySaveFunc(JSON.parse(localSave));

    localStorage[key] = JSON.stringify(saveData);

    return saveData;
  }

  public static copyAsText(list: ListState, ftlState: IFtlState) {
    const { ships, selectedFleets } = ftlState;
    const points: number = FtlService.calculateListTotal(ships);

    const hasListName = selectedFleets.length > 2 || list.name !== selectedFleets[0].name;

    const lines = [
      `++ ${hasListName ? list.name + " - " : ""}${selectedFleets
        .filter((x) => x.name !== "Common")
        .map((x) => `${x.name} (v${x.version})`)
        .join(", ")} [GFWF ${points}pts] ++\n`
    ];

    const writeShip = (ship: IFleetShipSelection) => {
      let shipText =
        (ship.count > 1 ? `${ship.count}x ` : "") +
        (ship.customName ? `${ship.customName} (${ship.name})` : ship.name);
      if (!ship.squadron) {
        shipText += ` [${ShipClass[ship.class]} ${ship.slots}]`;
      }
      shipText += ` | Mov ${ship.move}" Cru ${ship.cruise}" Eva ${ship.evade}+ Tou ${ship.tough}+`;
      shipText += ` | ${FtlService.getShipCost(ship)}pts`;
      const rules = [ship.hero?.name, ship.title?.name, ...ship.special].filter((x) => Boolean(x));
      if (rules.length > 0) {
        shipText += ` | ${rules.join(", ")}`;
      }

      shipText += `\n` + writeWeapon(FtlService.getTurret(ship));

      if (ship.weapons.length) {
        shipText += `\nWeapons: ${ship.weapons.map((x) => writeWeapon(x)).join(", ")}`;
      }

      if (ship.systems.length) {
        shipText += `\nSystems: ${ship.systems?.map((x) => x.name).join(", ")}`;
      }

      return shipText + `\n`;
    };

    const writeWeapon = (weapon: IShipWeapon) => {
      const getFacing = () => {
        switch (weapon.facing) {
          case ShipWeaponFacing.front:
            return "F";
          case ShipWeaponFacing.sides:
            return "S";
          case ShipWeaponFacing.rear:
            return "R";
          case ShipWeaponFacing.turret:
            return "T";
        }
      };
      return `[${getFacing()}] ${weapon.name} (Ran ${weapon.range}" / Att ${weapon.attacks} / Str ${
        weapon.str
      }${weapon.special.map((x) => `, ${x}`).join("")})`;
    };

    for (const ship of ships) {
      lines.push(writeShip(ship));
    }

    const fullText = lines.join("\n");
    navigator.clipboard.writeText(fullText).then(() => console.log("Copied to clipboard..."));
  }
}
